import { parseNumericValue } from "./numberFormatter";

export const ZAR8_LOCK_FLOW_STORAGE_NAME = "zar8-lock-flow-state";

export const ZAR8_YIELD_FLOW_TYPES = {
  lock: "lock",
  unlock: "unlock",
};

const ALLOWED_ZAR8_RETURN_PATHS = new Set(["/zar8", "/zar8/track"]);

export const ZAR8_LOCK_FLOW_STEPS = {
  amount: "amount",
  configuration: "configuration",
  summary: "summary",
  twoFactor: "twoFactor",
  processing: "processing",
  success: "success",
};

export const ZAR8_YIELD_ASSET_OPTIONS = [
  {
    id: "btc",
    label: "BTC",
    subtitle: "Bitcoin",
    apr: 0.092,
  },
  {
    id: "xaut",
    label: "XAUT (Tether Gold)",
    subtitle: "Ethereum",
    apr: 0.067,
  },
];

export const ZAR8_LOCK_DURATION_OPTIONS = [
  {
    id: "flex",
    label: "Flex",
    months: 1,
    multiplier: 0.35,
  },
  {
    id: "6m",
    label: "6 Months",
    months: 6,
    multiplier: 1,
  },
  {
    id: "12m",
    label: "12 Months",
    months: 12,
    multiplier: 1.15,
  },
  {
    id: "3y",
    label: "3 Years",
    months: 36,
    multiplier: 1.35,
  },
  {
    id: "5y",
    label: "5 Years",
    months: 60,
    multiplier: 1.6,
  },
];

export const createDefaultZar8LockFlowState = () => ({
  isOpen: false,
  flowType: ZAR8_YIELD_FLOW_TYPES.lock,
  step: ZAR8_LOCK_FLOW_STEPS.amount,
  amountInput: "",
  amountMode: "zar",
  selectedYieldAssetId: "",
  selectedDurationId: "",
  selectedUnlockPositionId: "",
});

const ALLOWED_ZAR8_LOCK_FLOW_STEPS = new Set(
  Object.values(ZAR8_LOCK_FLOW_STEPS)
);
const ALLOWED_ZAR8_YIELD_FLOW_TYPES = new Set(
  Object.values(ZAR8_YIELD_FLOW_TYPES)
);

const getSafeLockFlowStep = (step) =>
  ALLOWED_ZAR8_LOCK_FLOW_STEPS.has(step) ? step : ZAR8_LOCK_FLOW_STEPS.amount;

const getSafeYieldFlowType = (flowType) =>
  ALLOWED_ZAR8_YIELD_FLOW_TYPES.has(flowType)
    ? flowType
    : ZAR8_YIELD_FLOW_TYPES.lock;

const getSafeAmountMode = (amountMode) =>
  amountMode === "zar8" ? "zar8" : "zar";

const getSafeYieldAssetId = (assetId) =>
  ZAR8_YIELD_ASSET_OPTIONS.some((option) => option.id === assetId)
    ? assetId
    : "";

const getSafeDurationId = (durationId) =>
  ZAR8_LOCK_DURATION_OPTIONS.some((option) => option.id === durationId)
    ? durationId
    : "";

const sanitizePersistedLockFlowState = (state = {}) => ({
  ...createDefaultZar8LockFlowState(),
  isOpen: typeof state.isOpen === "boolean" ? state.isOpen : false,
  flowType: getSafeYieldFlowType(state.flowType),
  step: getSafeLockFlowStep(state.step),
  amountInput: typeof state.amountInput === "string" ? state.amountInput : "",
  amountMode: getSafeAmountMode(state.amountMode),
  selectedYieldAssetId: getSafeYieldAssetId(state.selectedYieldAssetId),
  selectedDurationId: getSafeDurationId(state.selectedDurationId),
  selectedUnlockPositionId:
    typeof state.selectedUnlockPositionId === "string"
      ? state.selectedUnlockPositionId
      : "",
});

export const readZar8LockFlowState = () => {
  if (typeof window === "undefined" || !window.sessionStorage) {
    return createDefaultZar8LockFlowState();
  }

  try {
    const storedState = window.sessionStorage.getItem(
      ZAR8_LOCK_FLOW_STORAGE_NAME
    );

    if (!storedState) {
      return createDefaultZar8LockFlowState();
    }

    return sanitizePersistedLockFlowState(JSON.parse(storedState));
  } catch (error) {
    return createDefaultZar8LockFlowState();
  }
};

export const persistZar8LockFlowState = (state) => {
  if (typeof window === "undefined" || !window.sessionStorage) {
    return;
  }

  window.sessionStorage.setItem(
    ZAR8_LOCK_FLOW_STORAGE_NAME,
    JSON.stringify(sanitizePersistedLockFlowState(state))
  );
};

export const clearZar8LockFlowState = () => {
  if (typeof window === "undefined" || !window.sessionStorage) {
    return;
  }

  window.sessionStorage.removeItem(ZAR8_LOCK_FLOW_STORAGE_NAME);
};

export const resolveZar8LockFlowState = (state, routeFlowType = null) => {
  const safeState = sanitizePersistedLockFlowState(state);

  if (!routeFlowType) {
    return safeState;
  }

  const hasFlowChanged = safeState.flowType !== routeFlowType;

  return {
    ...safeState,
    isOpen: true,
    flowType: routeFlowType,
    step: hasFlowChanged ? ZAR8_LOCK_FLOW_STEPS.amount : safeState.step,
  };
};

export const getSafeZar8YieldReturnPath = (
  returnPath,
  fallbackPath = "/zar8"
) => {
  if (ALLOWED_ZAR8_RETURN_PATHS.has(returnPath)) {
    return returnPath;
  }

  return ALLOWED_ZAR8_RETURN_PATHS.has(fallbackPath) ? fallbackPath : "/zar8";
};

export const getZar8YieldFlowPath = (
  flowBasePath,
  flowType,
  returnPath = null
) => {
  const normalizedBasePath = `${flowBasePath || "/zar8"}`
    .replace(/\/+$/, "")
    .trim();
  const resolvedPath = `${normalizedBasePath}/${getSafeYieldFlowType(
    flowType
  )}`;
  const safeReturnPath = returnPath
    ? getSafeZar8YieldReturnPath(returnPath)
    : null;

  if (!safeReturnPath || safeReturnPath === normalizedBasePath) {
    return resolvedPath;
  }

  return `${resolvedPath}?returnTo=${encodeURIComponent(safeReturnPath)}`;
};

export const getZar8YieldAssetOption = (assetId) =>
  ZAR8_YIELD_ASSET_OPTIONS.find((option) => option.id === assetId) || null;

export const getZar8DurationOption = (durationId) =>
  ZAR8_LOCK_DURATION_OPTIONS.find((option) => option.id === durationId) || null;

export const sanitizeZar8AmountInput = (value) => {
  const normalizedValue = `${value || ""}`.replace(/[^0-9.]/g, "");
  const [wholePart, ...decimalParts] = normalizedValue.split(".");
  const decimalPart = decimalParts.join("").slice(0, 2);

  if (decimalPart.length > 0) {
    return `${wholePart}.${decimalPart}`;
  }

  return wholePart;
};

const emitFlowEvent = (flowType, eventName, payload = {}) => {
  if (typeof window === "undefined") {
    return;
  }

  if (window.analytics?.track) {
    window.analytics.track(eventName, payload);
  }

  if (Array.isArray(window.dataLayer)) {
    window.dataLayer.push({
      event: eventName,
      ...payload,
    });
  }

  window.dispatchEvent(
    new CustomEvent(`zar8-${flowType}-flow`, {
      detail: {
        eventName,
        payload,
      },
    })
  );
};

export const emitZar8LockFlowEvent = (eventName, payload = {}) =>
  emitFlowEvent("lock", eventName, payload);

export const emitZar8UnlockFlowEvent = (eventName, payload = {}) => {
  emitFlowEvent("unlock", eventName, payload);
};

export const calculateEstimatedYieldValue = ({
  amountZar,
  assetId,
  durationId,
}) => {
  const assetOption = getZar8YieldAssetOption(assetId);
  const durationOption = getZar8DurationOption(durationId);

  if (!assetOption || !durationOption || amountZar <= 0) {
    return 0;
  }

  return (
    amountZar *
    assetOption.apr *
    (durationOption.months / 12) *
    durationOption.multiplier
  );
};

const normalizeDurationUnit = (unit) =>
  typeof unit === "string" ? unit.trim().toLowerCase() : "";

const normalizeDurationPeriod = (period) => {
  const parsed = Number.parseInt(`${period || ""}`, 10);
  return Number.isFinite(parsed) ? parsed : 0;
};

const getDurationIdFromParts = (period, unit) => {
  const normalizedUnit = normalizeDurationUnit(unit);
  const normalizedPeriod = normalizeDurationPeriod(period);

  if (!normalizedUnit) {
    return "";
  }

  if (normalizedUnit.includes("flex")) {
    return "flex";
  }

  if (normalizedUnit.startsWith("month")) {
    if (normalizedPeriod === 1) {
      return "flex";
    }

    return normalizedPeriod > 0 ? `${normalizedPeriod}m` : "";
  }

  if (normalizedUnit.startsWith("year")) {
    return normalizedPeriod > 0 ? `${normalizedPeriod}y` : "";
  }

  return "";
};

export const getZar8DurationIdFromParts = getDurationIdFromParts;

const formatUnlockTermLabel = ({ termId, period, unit, fallbackLabel }) => {
  const matchedDuration = getZar8DurationOption(termId);

  if (matchedDuration) {
    return matchedDuration.label;
  }

  const normalizedPeriod = normalizeDurationPeriod(period);
  const normalizedUnit = normalizeDurationUnit(unit);

  if (normalizedPeriod > 0 && normalizedUnit) {
    const labelUnit =
      normalizedPeriod === 1 ? normalizedUnit : `${normalizedUnit}s`;
    return `${normalizedPeriod} ${labelUnit.replace(/^\w/, (value) =>
      value.toUpperCase()
    )}`;
  }

  return fallbackLabel || "Select term";
};

const getZar8EntryMetadata = (entry) =>
  entry?.investmentId?.metadata ?? entry?.metadata ?? {};

export const getZar8UnlockPositionLockedAmount = (entry) =>
  Math.max(
    0,
    parseNumericValue(
      entry?.lockedAmount ??
        entry?.amountInvested ??
        entry?.investmentId?.investmentAmount ??
        entry?.valueInLocalCurrency ??
        entry?.valueInZar ??
        entry?.productId?.valueInLocalCurrency ??
        entry?.productId?.valueInZar ??
        entry?.productId?.value ??
        entry?.amount
    )
  );

export const buildZar8UnlockPositionOptions = (entries = []) => {
  const options = entries
    .filter(Boolean)
    .map((entry, index) => {
      const metadata = getZar8EntryMetadata(entry);
      const durationPeriod =
        metadata?.durationMonths ??
        entry?.productId?.duration?.period ??
        entry?.productId?.durationInYearOrMonth?.[0] ??
        entry?.duration?.period;
      const durationUnit = metadata?.durationMonths
        ? "months"
        : (entry?.productId?.duration?.unit ??
          entry?.productId?.durationInYearOrMonth?.[1] ??
          entry?.duration?.unit);
      const termId =
        metadata?.durationId ||
        getDurationIdFromParts(durationPeriod, durationUnit);
      const lockedAmount = getZar8UnlockPositionLockedAmount(entry);
      const fallbackLabel =
        metadata?.displayTitle ||
        metadata?.durationLabel ||
        entry?.productId?.name ||
        entry?.name ||
        `Yield position ${index + 1}`;
      const applicationId =
        entry?.investmentId?._id ||
        entry?.investmentId ||
        entry?.ledgerId ||
        entry?._id ||
        entry?.productId?._id ||
        "";
      const optionId = applicationId || `${termId || "position"}-${index}`;

      return {
        id: optionId,
        ledgerId: applicationId,
        termId,
        label: formatUnlockTermLabel({
          termId,
          period: durationPeriod,
          unit: durationUnit,
          fallbackLabel: metadata?.durationLabel || fallbackLabel,
        }),
        lockedAmount,
        feeType:
          entry?.productId?.withdrawalFee?.feeType ||
          entry?.productId?.withdrawalFees?.feeType ||
          "fixed",
        feeAmount: parseNumericValue(
          entry?.productId?.withdrawalFee?.feeAmount ??
            entry?.productId?.withdrawalFees?.$numberDecimal ??
            entry?.productId?.withdrawalFees ??
            0
        ),
      };
    })
    .filter(
      (option) => option.lockedAmount > 0 || option.label !== "Select term"
    );

  return options.sort((firstOption, secondOption) => {
    const firstDuration = getZar8DurationOption(firstOption.termId);
    const secondDuration = getZar8DurationOption(secondOption.termId);

    const firstMonths = firstDuration?.months ?? Number.MAX_SAFE_INTEGER;
    const secondMonths = secondDuration?.months ?? Number.MAX_SAFE_INTEGER;

    return firstMonths - secondMonths;
  });
};

export const calculateZar8UnlockPenaltyValue = ({
  amountZar,
  feeType,
  feeAmount,
}) => {
  if (amountZar <= 0 || feeAmount <= 0) {
    return 0;
  }

  if (typeof feeType === "string" && feeType.toLowerCase() === "percentage") {
    return amountZar * (feeAmount / 100);
  }

  return feeAmount;
};

export const calculateZar8UnlockSummary = ({
  amountZar,
  lockedAmountZar,
  feeType,
  feeAmount,
  quote,
}) => {
  const penaltyAmount = parseNumericValue(
    quote?.withdrawalFee ??
      quote?.fee ??
      calculateZar8UnlockPenaltyValue({
        amountZar,
        feeType,
        feeAmount,
      })
  );
  const netAmount = parseNumericValue(
    quote?.amountReceived ??
      quote?.netAmount ??
      Math.max(amountZar - penaltyAmount, 0)
  );
  const remainingLocked = Math.max(lockedAmountZar - amountZar, 0);

  return {
    penaltyAmount,
    netAmount,
    remainingLocked,
  };
};
